<template>
    <div class="w-100 mt-3">
        <div class="m-2">
            <b-row align-h="between">
                <b-col class="d-flex justify-content-between align-items-start" cols="12">
                    <div class="d-flex">
                        <div class="input-icon-wrapper search">
                            <div class="saysimple-icon embeded-icon search position-absolute text-secondary" />
                            <b-form-input
                                id="search-query"
                                v-model="searchQuery"
                                :placeholder="$t('paginator.search')"
                                class="bg-light round-medium"
                                @input="updateRouteQuery"
                            />
                        </div>

                        <contact-filters v-model="filters" />

                        <b-dropdown
                            v-model="perPage"
                            v-b-tooltip.hover.bottom="$t('paginator.number-of-items-per-page')"
                            :text="`${$t('paginator.entries-capitalized')}: ${perPage}`"
                            class="per-page-selector mx-50 round-medium"
                            no-caret
                            variant="light"
                        >
                            <b-dropdown-item
                                v-for="perPageOption in perPageOptions"
                                :key="perPageOption"
                                @click="perPage = perPageOption"
                            >
                                {{ perPageOption }}
                            </b-dropdown-item>
                        </b-dropdown>
                    </div>

                    <b-button
                        id="contacts-add"
                        :to="{ name: 'contacts-add' }"
                        class="text-nowrap round-medium ml-1"
                        variant="primary"
                    >
                        <font-awesome-icon icon="plus" />
                        <span class="text-nowrap d-none d-md-inline ml-50">{{ $t("contacts.Add contact") }}</span>
                    </b-button>
                </b-col>
            </b-row>
        </div>

        <div class="pwa-table-container">
            <b-table
                ref="refTable"
                :busy="isLoading"
                :empty-text="$t('paginator.no-matching-records-found')"
                :fields="tableColumns"
                :items="contacts"
                :no-sort-reset="true"
                :sort-by.sync="sortBy"
                :sort-desc.sync="isSortDirDesc"
                :table-busy="isLoading"
                primary-key="id"
                show-empty
            >
                <template #table-busy>
                    <div class="d-flex justify-content-center my-1">
                        <b-spinner />
                    </div>
                </template>

                <template #cell(name)="data">
                    <b-avatar
                        :text="resolveInitials(data.item.name)"
                        class="badge-minimal avatar-border-2 mr-1 round-large"
                        size="md"
                        variant="tertiary"
                    />

                    <strong>{{ data.item.name }}</strong>
                    <font-awesome-icon
                        v-if="data.item.isHighPriority"
                        v-b-tooltip.hover.bottom="$t('contacts.High priority')"
                        color="orange"
                        icon="crown"
                    />
                    <font-awesome-icon
                        v-if="data.item.isBlocked"
                        v-b-tooltip.hover.bottom="$t('entities.contact.is-blocked')"
                        class="text-danger ml-1"
                        icon="ban"
                    />
                </template>

                <template #cell(phone)="data">
                    <phone-number :phone-number="data.item.phone" />
                </template>
                <!-- Column: Actions -->
                <template #cell(actions)="data">
                    <div class="d-flex no-wrap">
                        <start-conversation
                            :button-attributes="{
                                class: 'overflow btn-icon mr-50',
                                'no-caret': true,
                                variant: 'light',
                            }"
                            :channels="channels"
                            :contact="data.item"
                            @start-chat="(channel) => startChat(data.item.id, channel.id, getUserData().id)"
                        >
                            <font-awesome-icon :title="$t('entities.conversation.start')" icon="comment-dots" />
                        </start-conversation>

                        <b-button
                            v-b-tooltip.hover.bottom="$t('Edit')"
                            :to="{
                                name: 'contacts-edit',
                                params: { id: data.item.id },
                            }"
                            class="btn-icon edit mr-50"
                            variant="light"
                        >
                            <font-awesome-icon icon="pen" />
                        </b-button>

                        <b-button
                            v-if="$can('delete', 'contacts')"
                            v-b-tooltip.hover.bottom="$t('Delete')"
                            class="btn-icon edit"
                            variant="light"
                            @click="localDeleteContact(data.item.id)"
                        >
                            <font-awesome-icon icon="trash" />
                        </b-button>
                    </div>
                </template>
            </b-table>
        </div>
        <div v-if="!isLoading" class="mx-2 mb-2 mt-4">
            <b-row>
                <b-col
                    class="d-flex align-items-center justify-content-center justify-content-sm-start"
                    cols="12"
                    sm="6"
                >
                    <span class="text-muted">
                        {{ $t("paginator.showing") }} {{ dataMeta.to }} {{ $t("paginator.of") }} {{ dataMeta.of }}
                        {{ $t("paginator.entries") }}
                    </span>
                </b-col>

                <b-col class="d-flex align-items-center justify-content-center justify-content-sm-end" cols="12" sm="6">
                    <b-pagination
                        v-model="page"
                        :per-page="perPage"
                        :total-rows="count"
                        class="mb-0 mt-1 mt-sm-0"
                        first-number
                        last-number
                        next-class="next-item"
                        prev-class="prev-item"
                        size="sm"
                    >
                        <template #prev-text>
                            <font-awesome-icon icon="chevron-left" size="lg" />
                        </template>
                        <template #next-text>
                            <font-awesome-icon icon="chevron-right" size="lg" />
                        </template>
                    </b-pagination>
                </b-col>
            </b-row>
        </div>
    </div>
</template>

<script>
import {
    BAvatar,
    BButton,
    BCol,
    BDropdown,
    BDropdownItem,
    BFormInput,
    BModal,
    BPagination,
    BRow,
    BSpinner,
    BTable,
    VBTooltip,
} from "bootstrap-vue";
import useChannels from "@/composables/useChannels";
import useContacts from "@/views/modules/contacts/useContacts";
import useFilters from "@/views/modules/contacts/useFilters";
import { computed, ref, watch } from "@vue/composition-api";
import { getUserData } from "@/auth/getUserData";
import { useToast } from "vue-toastification/composition";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import router from "@/router";
import { resolveInitials } from "@/utils/users";
import PhoneNumber from "@/components/phoneNumber";
import StartConversation from "@/components/startConversation";
import ContactFilters from "@/views/components/contact-lists/contactFilters.vue";
import i18n from "@/libs/i18n";
import ToastificationContent from "@/layouts/components/toastification/toastificationContent.vue";

export default {
    components: {
        StartConversation,
        PhoneNumber,
        BModal,
        BAvatar,
        BButton,
        BCol,
        BDropdown,
        BDropdownItem,
        BFormInput,
        BPagination,
        BRow,
        BTable,
        BSpinner,
        ContactFilters,
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    setup(_, context) {
        const bvModal = context.root.$bvModal;
        const toast = useToast();
        const { t } = useI18nUtils();

        const { channels, fetchChannels } = useChannels();
        const { contacts, count, fetchContacts, isLoading, filters, deleteContact } = useContacts();
        const { page, perPage, isSortDirDesc, sortBy, searchQuery, perPageOptions, updateRouteQuery } = useFilters();

        const localDeleteContact = (contactId) => {
            bvModal
                .msgBoxConfirm(i18n.t("confirm.Are you sure?"), {
                    title: i18n.t("confirm.Are you sure?"),
                    cancelTitle: i18n.t("confirm.Cancel"),
                    cancelVariant: "light",
                    okTitle: i18n.t("confirm.Yes, delete"),
                    hideHeaderClose: false,
                    headerCloseVariant: "light",
                    centered: true,
                })
                .then(async (result) => {
                    if (!result) {
                        return;
                    }

                    await deleteContact(contactId)
                        .then(() => {
                            toast({
                                component: ToastificationContent,
                                props: {
                                    title: i18n.t("contacts.contact-deleted"),
                                    text: i18n.t("contacts.contact-deleted"),
                                    icon: "check",
                                    variant: "success",
                                },
                            });
                        })
                        .catch(() => {
                            toast({
                                component: ToastificationContent,
                                props: {
                                    title: i18n.t("contacts.could-not-delete-contact"),
                                    text: i18n.t("contacts.could-not-delete-contact"),
                                    icon: "times",
                                    variant: "danger",
                                },
                            });
                        });
                });
        };

        const refTable = ref(null);
        const tableColumns = [
            { key: "name", label: t("entities.contact.Name"), sortable: true },
            { key: "email", label: t("entities.contact.Email"), sortable: true },
            { key: "phone", label: t("entities.contact.Phone"), sortable: true },
            { key: "company", label: t("entities.contact.Company"), sortable: true },
            { key: "reference", label: t("entities.contact.Reference"), sortable: true },
            { key: "actions", label: t("Actions") },
        ];

        const dataMeta = computed(() => {
            const localItemsCount = refTable.value ? refTable.value.localItems.length : 0;

            return {
                from: perPage.value * (page.value - 1) + (localItemsCount ? 1 : 0),
                to: perPage.value * (page.value - 1) + localItemsCount,
                of: count.value,
            };
        });

        watch(
            filters,
            () => {
                fetchContacts();
            },
            { deep: true }
        );

        const startChat = (contactId, channelId, userId) => {
            router.replace({
                name: "start-conversation",
                query: {
                    contactId,
                    channelId,
                    userId,
                },
            });
        };

        watch([page, perPage, sortBy], () => {
            fetchContacts();
        });

        const queryTimeout = ref(null);
        watch(searchQuery, (query) => {
            if (query.length === 1) {
                return;
            }

            if (queryTimeout.value) {
                clearTimeout(queryTimeout.value);
            }

            queryTimeout.value = setTimeout(() => {
                fetchContacts();
            }, 500);
        });

        Promise.all([
            fetchContacts(),
            fetchChannels({
                $or: [
                    { platform: { $eq: "whatsapp" } },
                    { platform: { $eq: "email" } },
                    { platform: { $eq: "test" } },
                    { platform: { $eq: "sms" } },
                ],
            }),
        ]).catch(() => toast.error(t("toast.read.error")));

        return {
            channels,
            contacts,
            count,
            filters,
            dataMeta,
            isSortDirDesc,
            page,
            perPage,
            perPageOptions,
            refTable,
            searchQuery,
            sortBy,
            tableColumns,
            isLoading,
            // isLoaded concern apps
            //clickablePhoneNumbersFeature,
            // Methods
            getUserData,
            updateRouteQuery,
            startChat,
            resolveInitials,
            localDeleteContact,
        };
    },
};
</script>
